import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import { isValidTag, normalizeTag } from "../../utils/messageTemplateApi";

const useStyles = makeStyles({
  touch: { minHeight: "44px" },
  field: { marginTop: "12px" },
});

/**
 * Asks for a variable the code does not supply yet. The tag is inserted as a plain `{{tag}}` and
 * sent with the next save as a request; until a developer maps it, it is sent empty.
 */
const RequestVariableDialog = ({ open, variables, onCancel, onConfirm }) => {
  const classes = useStyles();
  const [tag, setTag] = useState("");
  const [description, setDescription] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setTag("");
      setDescription("");
      setTouched(false);
    }
  }, [open]);

  const normalized = normalizeTag(tag);
  const existing = (variables || []).find((v) => v.name === normalized);
  let error = null;
  if (!normalized) error = "Enter a name, for example diver.coachEmail";
  else if (!isValidTag(normalized))
    error = "Letters, digits and _ only, with dots between parts";
  else if (existing)
    error = `Already available as "${existing.label}"; insert it from the list instead`;

  const submit = (event) => {
    event.preventDefault();
    setTouched(true);
    if (error) return;
    onConfirm({ tag: normalized, description: description.trim() || null });
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      aria-labelledby="mt-request-variable-title"
      maxWidth="sm"
      fullWidth
    >
      <form onSubmit={submit} noValidate>
        <DialogTitle id="mt-request-variable-title">
          Request a variable
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Use this for a value the list does not offer yet. It is inserted now
            and sent to developers when you save. Until they connect it, real
            messages show nothing in its place; the preview shows it as
            [[name?]].
          </DialogContentText>
          <TextField
            autoFocus
            required
            fullWidth
            id="mt-request-tag"
            label="Variable name"
            placeholder="diver.coachEmail"
            variant="outlined"
            className={classes.field}
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            onBlur={() => setTouched(true)}
            error={touched && Boolean(error)}
            helperText={
              touched && error
                ? error
                : "Group it under an existing name where it fits: diver., coach., team., meet., event."
            }
            inputProps={{ maxLength: 200, spellCheck: false }}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            id="mt-request-description"
            label="What should it contain?"
            placeholder="The email address of the diver's coach"
            variant="outlined"
            className={classes.field}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            inputProps={{ maxLength: 1000 }}
          />
        </DialogContent>
        <DialogActions>
          <Button className={classes.touch} onClick={onCancel}>
            Cancel
          </Button>
          <Button
            className={classes.touch}
            type="submit"
            color="primary"
            variant="contained"
          >
            Insert and request
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

RequestVariableDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  variables: PropTypes.array,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

RequestVariableDialog.defaultProps = {
  variables: [],
};

export default RequestVariableDialog;
